<script setup lang="ts">
import { defineEmits, nextTick, ref } from "vue";
import { useI18n } from "vue-i18n";

const emit = defineEmits<{
  (e: "close"): void;
}>();

const { t } = useI18n();

const inputEl = ref<HTMLInputElement | null>(null);
const rawPhone = ref<string>("+38(___)___-__-__");
const showFeedback = ref<boolean>(false);

function onFocus() {
  requestAnimationFrame(() => {
    const el = inputEl.value;
    if (!el) return;
    el.value = rawPhone.value;
    const pos = rawPhone.value.indexOf("_");
    if (pos >= 0) el.setSelectionRange(pos, pos);
  });
}

function onKeydown(e: KeyboardEvent) {
  const el = inputEl.value;
  e.preventDefault();
  if (!el) return;

  const key = e.key;

  if (/^\d$/.test(key)) {
    const pos = rawPhone.value.indexOf("_");
    if (pos === -1) return;

    const chars = rawPhone.value.split("");
    chars[pos] = key;
    rawPhone.value = chars.join("");
  }

  if (key === "Backspace" || key === "Delete") {
    const selStart = el.selectionStart ?? 0;
    const selEnd = el.selectionEnd ?? 0;
    const chars = rawPhone.value.split("");

    const protectedIndices = [1, 2, 3];

    if (selEnd > selStart) {
      for (let i = selStart; i < selEnd; i++) {
        if (/\d/.test(chars[i]) && !protectedIndices.includes(i)) {
          chars[i] = "_";
        }
      }
      rawPhone.value = chars.join("");
    } else {
      for (let i = selStart - 1; i >= 0; i--) {
        if (/\d/.test(chars[i]) && !protectedIndices.includes(i)) {
          chars[i] = "_";
          rawPhone.value = chars.join("");
        }
      }
    }
  }

  // Ставим курсор на следующий "_"
  nextTick(() => {
    const nextPos = rawPhone.value.indexOf("_");
    const targetPos = nextPos >= 0 ? nextPos : rawPhone.value.length;
    el.setSelectionRange(targetPos, targetPos);
  });
}

function close() {
  emit("close");
}

function sendPhone() {
  const phoneDigits = rawPhone.value.replace(/[^0-9]/g, "");

  console.log(phoneDigits); // It will be api call here

  showFeedback.value = true;
}
</script>

<template>
  <teleport to="body">
    <div
      class="fixed inset-0 bg-black/50 flex items-center justify-center z-[200]"
      @click.self="close"
    >
      <div
        class="bg-white p-[30px] rounded-[40px] text-center shadow-lg w-[378px] min-h-[309px] flex flex-col relative"
      >
        <!-- Close icon -->
        <div
          class="size-8 absolute top-[15px] right-[15px] bg-hover rounded-full flex items-center justify-center cursor-pointer"
          @click="close"
        >
          <svg
            width="10"
            height="10"
            viewBox="0 0 10 10"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            class="text-active"
          >
            <path
              d="M0.226473 8.6205C-0.0734506 8.92042 -0.0795715 9.45294 0.232594 9.76511C0.55088 10.0773 1.0834 10.0711 1.3772 9.77735L5.00076 6.15379L8.61821 9.77123C8.92425 10.0773 9.45065 10.0773 9.76282 9.76511C10.075 9.44682 10.075 8.92655 9.76894 8.6205L6.15149 5.00306L9.76894 1.3795C10.075 1.07346 10.0811 0.547059 9.76282 0.234894C9.45065 -0.0772704 8.92425 -0.0772704 8.61821 0.228774L5.00076 3.84621L1.3772 0.228774C1.0834 -0.0711496 0.544759 -0.0833913 0.232594 0.234894C-0.0795715 0.547059 -0.0734506 1.0857 0.226473 1.3795L3.84392 5.00306L0.226473 8.6205Z"
              fill="#EE441A"
            />
          </svg>
        </div>

        <img
          src="@/assets/icons/check-mark%201.svg"
          class="size-20 mb-6 mx-auto text-[#D39F47]"
          alt="succeed logo"
          v-if="showFeedback"
        />
        <h3
          class="sf-pro font-semibold text-2xl leading-[32px] text-brown mb-4 whitespace-pre-line"
        >
          {{
            showFeedback
              ? t("personal_consult_modal.feedback_title")
              : t("personal_consult_modal.title")
          }}
        </h3>
        <p class="sf-pro font-normal text-base leading-100p text-muted mb-6">
          {{
            showFeedback
              ? t("personal_consult_modal.feedback_text")
              : t("personal_consult_modal.text")
          }}
        </p>
        <input
          v-if="!showFeedback"
          name="phone_number"
          ref="inputEl"
          type="tel"
          :value="rawPhone"
          placeholder="+38(___)___-__-__"
          @focus="onFocus"
          @click.stop="onFocus"
          @keydown="onKeydown"
          class="bg-secondary rounded-[14px] border border-divider focus:border-divider focus:outline-none w-full h-[52px] sf-pro font-normal text-base leading-[24px] text-muted pl-4 py-[14px]"
          required
        />
        <button
          type="submit"
          class="bg-orange-gradient h-[50px] text-white rounded-[200px] sf-rpo font-semibold text-[17px] leading-[22px] mt-auto"
          @click="showFeedback ? close() : sendPhone()"
        >
          {{
            showFeedback ? t("buttons.ok") : t("personal_consult_modal.send")
          }}
        </button>
      </div>
    </div>
  </teleport>
</template>
